import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Pencil, Building, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';

const LandmarksPopup = ({ location, onClose, supabase, onSuccess }) => {
  // Parse landmarks from location or initialize as empty array
  const [landmarks, setLandmarks] = useState(location.landmarks || []);
  const [newLandmark, setNewLandmark] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  // When popup opens, focus on the input field
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Focus on edit input when edit mode is enabled
  useEffect(() => {
    if (editIndex !== null) {
      const timer = setTimeout(() => {
        editInputRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [editIndex]);

  // Handle click outside to close popup
  const popupRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleAddLandmark = () => {
    if (!newLandmark.trim()) return;
    
    // Don't add duplicates
    if (landmarks.includes(newLandmark.trim())) {
      toast("This landmark is already in the list", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }
    
    setLandmarks([...landmarks, newLandmark.trim()]);
    setNewLandmark('');
    inputRef.current?.focus();
  };

  const handleRemoveLandmark = (index) => {
    const updatedLandmarks = [...landmarks];
    updatedLandmarks.splice(index, 1);
    setLandmarks(updatedLandmarks);
  };

  const handleEditLandmark = (index) => {
    setEditIndex(index);
    setNewLandmark(landmarks[index]);
  };

  const handleSaveEdit = () => {
    if (!newLandmark.trim()) {
      toast("Landmark cannot be empty", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }
    
    // Don't add duplicates (except the current one being edited)
    const otherLandmarks = landmarks.filter((_, i) => i !== editIndex);
    if (otherLandmarks.includes(newLandmark.trim())) {
      toast("This landmark is already in the list", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }
    
    const updatedLandmarks = [...landmarks];
    updatedLandmarks[editIndex] = newLandmark.trim();
    setLandmarks(updatedLandmarks);
    setNewLandmark('');
    setEditIndex(null);
    inputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setNewLandmark('');
    setEditIndex(null);
    inputRef.current?.focus();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('vapi_location_settings')
        .update({ landmarks: landmarks })
        .eq('id', location.id);
      
      if (error) throw error;
      
      toast.success('Landmarks saved successfully');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving landmarks:', err);
      toast.error('Failed to save landmarks');
    } finally {
      setIsSaving(false);
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    },
    removed: {
      opacity: 0,
      x: -50,
      transition: { duration: 0.2 }
    }
  };

  // Portal the modal to ensure it's at the root level of the DOM
  const popupContent = (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      >
        <motion.div
          ref={popupRef}
          variants={popupVariants}
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <Building size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Help customers find you with local landmarks</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  When customers ask 'Are you near Ray's Shrimp House?' your AI can say 'Yes, we're right next to it!' instead of just giving an address.
                </p>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </motion.button>
          </motion.div>
          
          {/* Content */}
          <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
            {/* Input form */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="w-full"
            >
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    ref={editIndex === null ? inputRef : editInputRef}
                    type="text"
                    value={newLandmark}
                    onChange={(e) => setNewLandmark(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (editIndex !== null) {
                          handleSaveEdit();
                        } else {
                          handleAddLandmark();
                        }
                      } else if (e.key === 'Escape' && editIndex !== null) {
                        handleCancelEdit();
                      }
                    }}
                    placeholder={editIndex !== null 
                      ? "Edit landmark" 
                      : "Enter landmark (e.g., 'Next to Town Hall')"
                    }
                    className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
                      focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
                      placeholder:text-gray-400"
                    disabled={isSaving}
                  />
                </div>
                
                {editIndex !== null ? (
                  <div className="flex space-x-1 sm:space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleSaveEdit}
                      disabled={!newLandmark.trim() || isSaving}
                      className="h-10 sm:h-11 px-3 sm:px-4 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Update
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="h-10 sm:h-11 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm
                        hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </motion.button>
                  </div>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAddLandmark}
                    disabled={!newLandmark.trim() || isSaving}
                    className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl bg-black text-white
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </motion.button>
                )}
              </div>
            </motion.div>
            
            {/* Landmarks list */}
            <div className="space-y-1.5 sm:space-y-2">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center justify-between"
              >
                <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                  {landmarks.length} Landmark{landmarks.length !== 1 ? 's' : ''}
                </h3>
              </motion.div>
              
              <AnimatePresence>
                {landmarks.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 
                      rounded-xl border border-dashed border-gray-200 text-center"
                  >
                    <Building size={20} className="text-gray-300 mb-2 sm:mb-3 sm:w-6 sm:h-6" />
                    <p className="text-gray-500 text-xs sm:text-sm">
                      No landmarks added yet
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                      Add nearby landmarks that callers might reference
                    </p>
                  </motion.div>
                ) : (
                  <motion.ul 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-xl overflow-hidden border border-gray-100 divide-y divide-gray-100"
                  >
                    <AnimatePresence>
                      {landmarks.map((landmark, index) => (
                        <motion.li 
                          key={`landmark-${landmark}-${index}`}
                          custom={index}
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          exit="removed"
                          className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white
                            hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-gray-800 text-xs sm:text-sm font-light mr-2">{landmark}</span>
                          <div className="flex items-center space-x-1">
                            <motion.button
                              whileHover={{ scale: 1.1, backgroundColor: '#f0f9ff' }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditLandmark(index)}
                              className="text-gray-500 hover:text-blue-500 rounded-full w-7 h-7 sm:w-8 sm:h-8 
                                flex items-center justify-center hover:bg-blue-50"
                              disabled={isSaving || editIndex !== null}
                              aria-label={`Edit ${landmark}`}
                            >
                              <Pencil size={14} className="sm:w-[15px] sm:h-[15px]" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1, backgroundColor: '#fef2f2' }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleRemoveLandmark(index)}
                              className="text-gray-500 hover:text-red-500 rounded-full w-7 h-7 sm:w-8 sm:h-8 
                                flex items-center justify-center hover:bg-red-50"
                              disabled={isSaving || editIndex !== null}
                              aria-label={`Remove ${landmark}`}
                            >
                              <Trash2 size={14} className="sm:w-[15px] sm:h-[15px]" />
                            </motion.button>
                          </div>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          {/* Footer */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-5 sm:px-7 py-4 bg-gray-50 flex justify-end gap-2 sm:gap-3 sticky bottom-0 border-t"
          >
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: '#f9fafb' }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm font-medium
                bg-white hover:bg-gray-50 transition-colors"
              disabled={isSaving || editIndex !== null}
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isSaving || editIndex !== null}
              className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Saving...
                </div>
              ) : (
                "Save Changes"
              )}
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Use a portal to render the modal at the document level
  return createPortal(popupContent, document.body);
};

export default LandmarksPopup; 